import "./FileInfo.scss";

import data from "./data";
import formatBytes from "./helpers/formatBytes";

const FieldMap = new Map([
    [ "name", [ "text", "fieldName", undefined, true ] ],
    [ "email", [ "email", "fieldEmail", undefined, true ] ],
    [ "caption", [ "textarea", "fieldCaption", undefined, false ] ],
    [ "description", [ "textarea", "fieldDescription", undefined, false ] ],
    [ "alt", [ "text", "fieldAlt", undefined, false ] ],
    [ "custom_url", [ "url", "fieldCustomUrl", undefined, false ] ],
    [ "custom_target", [ "select", "fieldCustomTarget", "customTargets", false ] ],
    [ "tags", [ "text", "fieldTags", undefined, false ] ]
]);

const EMPTY_PREVIEW = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

/**
 * A class to create and manage information for a single file upload.
 */
export default class FileInfo {
    /**
     * Create a new instance of the class.
     * @param {FileDialog} parent
     * @param {object} uploadItem
     */
    constructor( parent, uploadItem ) {
        this.onRemoveClicked = this.onRemoveClicked.bind( this );

        this.parentFileDialog = parent;
        this.parentForm = parent.parentForm;
        this.uploadItem = uploadItem;
        this.file = uploadItem.file;
        this.element = this.createElement( uploadItem );
        this.thumbnail = this.element.querySelector( ".fg-feu-file-thumbnail" );
        this.buttonRemove = this.element.querySelector( ".fg-feu-file-remove" );
        this.buttonRemove?.addEventListener( "click", this.onRemoveClicked, { once: true } );
    }

    get hasBlockingError() {
        return typeof this.uploadItem.error === "string" && this.uploadItem.error.length > 0;
    }

    getBlockingError() {
        return this.hasBlockingError ? this.uploadItem.error : "";
    }

    createElement( uploadItem ) {
        const { file, kind } = uploadItem;
        const element = document.createElement( "section" );
        element.classList.add( "fg-feu-file" );
        if ( kind === "video" ) {
            element.classList.add( "fg-feu-file-video" );
        }
        if ( this.hasBlockingError ) {
            element.classList.add( "fg-feu-file-invalid" );
        }

        element.innerHTML = `<figure class="fg-feu-file-image">
    <div class="fg-feu-file-thumbnail-wrap">
        <img class="fg-feu-file-thumbnail" alt="${ file.name }" src="${ uploadItem.previewUrl || EMPTY_PREVIEW }" draggable="false"/>
        <span class="fg-feu-file-badge">${ this.getBadgeLabel() }</span>
    </div>
    <figcaption class="fg-feu-file-info">
        <label class="fg-feu-file-detail">
            ${ this.parentForm.i18n.fileName }: <span>${ file.name }</span>
        </label>
        <label class="fg-feu-file-detail">
            ${ this.parentForm.i18n.fileType }: <span>${ file.type || file.name.split( "." ).pop()?.toUpperCase() || "Unknown" }</span>
        </label>
        <label class="fg-feu-file-detail">
            ${ this.parentForm.i18n.fileSize }: <span class="fg-feu-file-size">${ formatBytes( file.size ) }</span>
        </label>
        <label class="fg-feu-file-detail">
            ${ this.parentForm.i18n.fileDimensions }: <span class="fg-feu-file-dimensions">? x ?</span>
        </label>
        ${ this.hasBlockingError ? `<p class="fg-feu-file-error">${ this.uploadItem.error }</p>` : "" }
        <button type="button" class="fg-feu-file-remove">${ this.parentForm.i18n.fileRemove }</button>
    </figcaption>
</figure>`;

        const dimensions = element.querySelector( ".fg-feu-file-dimensions" );
        if ( uploadItem.dimensions?.width && uploadItem.dimensions?.height ) {
            dimensions.textContent = `${ uploadItem.dimensions.width } x ${ uploadItem.dimensions.height }`;
        } else {
            const thumb = element.querySelector( ".fg-feu-file-thumbnail" );
            thumb.onload = () => {
                thumb.onload = null;
                dimensions.textContent = `${ thumb.naturalWidth } x ${ thumb.naturalHeight }`;
            };
        }

        const fields = this.createFieldSetElement();
        if ( fields instanceof HTMLFieldSetElement ) element.append( fields );
        return element;
    }

    getBadgeLabel() {
        if ( this.hasBlockingError ) {
            return this.parentForm.i18n.fileStatusInvalid;
        }

        if ( this.uploadItem.kind === "video" ) {
            return this.parentForm.i18n.fileStatusVideo;
        }

        return "";
    }

    createFieldSetElement() {
        if ( !this.parentForm.hasFields ) return null;

        const fieldset = document.createElement( "fieldset" );
        fieldset.classList.add( "fg-feu-file-fields" );

        const isFirstFile = this.parentFileDialog.fileInfos.size === 0;

        for ( const field of this.parentForm.fields ) {
            if ( !FieldMap.has( field ) ) continue;
            const [ type, i18nName, dataName, firstOnly ] = FieldMap.get( field );

            let input;
            if ( firstOnly && !isFirstFile ) {
                input = document.createElement( "input" );
                input.type = "hidden";
                if ( this.parentFileDialog._firstOnlyValues.has( field ) ) {
                    input.value = this.parentFileDialog._firstOnlyValues.get( field );
                }
            } else {
                switch ( type ) {
                    case "textarea":
                        input = document.createElement( "textarea" );
                        input.rows = 3;
                        break;
                    case "text":
                    case "email":
                    case "url":
                        input = document.createElement( "input" );
                        input.type = type;
                        break;
                    case "select":
                        input = document.createElement( "select" );
                        const options = Array.isArray( data[ dataName ] ) ? data[ dataName ] : [];
                        options.forEach( ( optionData, index ) => {
                            const option = document.createElement( "option" );
                            option.value = optionData.value;
                            option.textContent = optionData.label;
                            if ( index === 0 ) option.selected = true;
                            input.append( option );
                        } );
                        break;
                }

                input.classList.add( "fg-feu-file-field-input" );
                if ( this.parentForm.requiredFields.includes( field ) ) {
                    input.required = true;
                    input.setAttribute( "aria-required", "true" );
                }

                if ( firstOnly && isFirstFile ) {
                    input.addEventListener( "input", event => {
                        this.parentFileDialog.syncFirstOnlyFields( field, event.target.value );
                    } );
                }
            }

            if ( !input ) continue;

            input.dataset.field = field;
            input.dataset.firstOnly = firstOnly ? "yes" : "no";

            if ( !firstOnly || isFirstFile ) {
                const label = document.createElement( "label" );
                label.classList.add( "fg-feu-file-field" );

                const span = document.createElement( "span" );
                span.classList.add( "fg-feu-file-field-name" );
                span.textContent = this.parentForm.i18n[ i18nName ];

                if ( this.parentForm.requiredFields.includes( field ) ) {
                    const required = document.createElement( "span" );
                    required.classList.add( "fg-feu-required" );
                    required.textContent = " *";
                    required.setAttribute( "aria-label", "required" );
                    span.append( required );
                }

                label.append( span, input );
                fieldset.append( label );
            } else {
                fieldset.append( input );
            }
        }

        return fieldset;
    }

    appendToFormData( formData, index, isFirstValid ) {
        formData.append( `foogallery_images[${ index }]`, this.file, this.file.name );
        formData.append( `foogallery_file_kinds[${ index }]`, this.uploadItem.kind );

        if ( this.uploadItem.kind === "video" && this.uploadItem.posterFile instanceof File ) {
            formData.append( `foogallery_video_posters[${ index }]`, this.uploadItem.posterFile, this.uploadItem.posterFile.name );
        }

        for ( const field of this.parentForm.fields ) {
            const input = this.element.querySelector( `[data-field="${ field }"]` );
            if ( !( input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement || input instanceof HTMLSelectElement ) ) {
                continue;
            }

            if ( input.dataset.firstOnly === "yes" ) {
                if ( isFirstValid ) {
                    formData.append( field, input.value );
                }
                continue;
            }

            formData.append( `${ field }[${ index }]`, input.value );
        }
    }

    remove() {
        this.buttonRemove?.removeEventListener( "click", this.onRemoveClicked );
        this.element.remove();
        this.parentFileDialog.fileInfos.delete( this.element );
    }

    onRemoveClicked( event ) {
        event.preventDefault();
        const elements = Array.from( this.parentFileDialog.body.querySelectorAll( ".fg-feu-file" ) );
        const index = elements.indexOf( this.element );
        if ( index !== -1 ) {
            this.parentForm.removeUploadItem( index );
            this.remove();
            this.parentFileDialog.checkFilesChanged();
        }
    }
};
